"use client";
import Link from "next/link";
import { Separator } from "@/components/ui/separator";
import { Logo } from "../logo";
import {
  NavigationMenu,
  NavigationMenuList,
  NavigationMenuItem,
} from "@/components/ui/navigation-menu";
import { footerLinks, socials } from "@/data/footer";
import { Button } from "../ui/button";
import { Subscribe } from "../footer/subscribe";
import { Reveal } from "../reveal";
import { getHref, MenuItemType } from "../navigation/navmenu";
import { Slug } from "@shared/sanity/types";

export type CleanMenuItemType = Omit<MenuItemType, "link" | "children"> & {
  link:
    | {
        type: "internal";
        internal: { title: string | null; slug: Slug };
        external?: null;
      }
    | {
        type: "external";
        external: string;
        internal?: null;
      }
    | {
        type: null;
        internal?: null;
        external?: null;
      }
    | null;
  children?: CleanMenuItemType[] | null;
};

interface FooterClientProps {
  menuItems: CleanMenuItemType[];
}

function MenuItem({ item }: { item: CleanMenuItemType }) {
  const href = getHref(item.link);

  if (item.children && item.children.length > 0) {
    return (
      <Reveal>
        <h5 className="capitalize font-medium mb-6">{item.text}</h5>
        <ul className="flex flex-col gap-3">
          {(item.children ?? [])
            .sort((a, b) => b.text.length - a.text.length)
            .map((child) => (
              <MenuItem item={child} key={child._key} />
            ))}
        </ul>
      </Reveal>
    );
  }

  return (
    <Link
      href={href}
      className="capitalize text-xs md:text-base break-words opacity-75"
      aria-label={item.ariaLabel}
    >
      {item.text}
    </Link>
  );
}

export function FooterClient({ menuItems }: FooterClientProps) {
  // Filter items with children
  const itemsWithChildren = menuItems
    .filter((item) => item.children && item.children.length > 0)
    .sort((a, b) => {
      const x = a.children ? a.children.length : 0;
      const y = b.children ? b.children.length : 0;
      return y - x;
    });

  // Filter items without children
  const itemsWithoutChildren = menuItems.filter(
    (item) => !item.children || item.children.length === 0,
  );

  return (
    <footer className="dark bg-primary-900 text-primary-text-50 font-outfit py-16 md:py-24 lg:py-28 px-6 md:px-8">
      <div className="flex flex-col max-w-screen-2xl mx-auto gap-12 md:gap-16">
        {/* Main Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Navigation Links */}
          <div className="lg:col-span-8 xl:col-span-8">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 md:gap-8 lg:gap-10">
              {itemsWithChildren.map((item) => (
                <MenuItem item={item} key={item._key} />
              ))}
              {itemsWithoutChildren.length > 0 && (
                <Reveal>
                  <ul className="flex flex-col gap-3">
                    {itemsWithoutChildren.map((item) => (
                      <MenuItem item={item} key={item._key} />
                    ))}
                  </ul>
                </Reveal>
              )}
            </div>
          </div>

          <div className="lg:col-span-4 xl:col-span-4 lg:pl-8">
            <Subscribe />
          </div>
        </div>

        <Separator className="bg-foreground opacity-20" />

        <div className="flex flex-col md:flex-row justify-between items-center gap-8 md:gap-6">
          {/* Logo */}
          <div className="order-1 md:order-1">
            <Logo theme="light" />
          </div>

          <NavigationMenu className="order-3 md:order-2">
            <NavigationMenuList className="flex flex-row capitalize gap-4 md:gap-8 lg:gap-10 text-center">
              {footerLinks.map((link) => (
                <NavigationMenuItem key={link.title}>
                  <Link
                    href={link.href}
                    className="text-sm md:text-base hover:opacity-75 transition-opacity"
                  >
                    {link.title}
                  </Link>
                </NavigationMenuItem>
              ))}
            </NavigationMenuList>
          </NavigationMenu>

          <div className="flex flex-row gap-3 order-2 md:order-3">
            {socials.map((social) => (
              <Button
                size="icon"
                radius="full"
                variant="ghost"
                key={social.platform}
                asChild
                className="w-[40px] h-[40px] md:w-[35px] md:h-[35px] p-2 border-[1.5px] border-white border-opacity-25 hover:bg-white hover:bg-opacity-10 transition-all"
              >
                <a
                  href={social.link}
                  aria-label={social.platform}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <social.icon />
                </a>
              </Button>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
}
